// Página pública /vendor-pay?t=TOKEN: el vendedor de The Village escoge sus
// días, acepta las condiciones y paga el depósito (25 %) o el total; después
// usa el mismo enlace para pagar el balance.
import { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import moment from 'moment';
import {
  Container,
  Paper,
  Typography,
  Stack,
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Grid,
  MenuItem,
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Logo from '../../components/logo';
import {
  FN_URLS,
  METHOD_LABELS,
  KIND_LABELS,
  money,
  fmtDateEs,
  computeTotals,
  callFn,
  DOC_TYPES,
  DOC_KEYS,
  taxBreakdown,
  fileToBase64,
  missingDocs,
} from '../../utils/csccVendors';

const stripePromise = loadStripe('pk_live_51Q5pO5RqULsyaFnGO4c7JjHvqYv9Eu4FovYOlgCfuWZyrpYgn33p1cZavi28z41Nxpo3PiKLbscqL7c2ezQUFux400HaYeh7o6');

const ELECTRICITY = ['Generador propio', 'Batería / estación de energía portátil', 'No requiere electricidad', 'Otro'];
const WATER = ['Traerá reserva propia', 'No requiere agua para su operación'];

function CardForm({ token, kind, amountCents, label, onPaid, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const pay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    try {
      const { clientSecret, paymentIntentId } = await callFn(FN_URLS.createIntent, { token, kind });
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (error) throw new Error(error.message || 'No se pudo procesar la tarjeta.');
      if (paymentIntent.status !== 'succeeded') throw new Error('El pago no se completó. Intenta de nuevo.');
      const r = await callFn(FN_URLS.confirmPayment, { token, paymentIntentId: paymentIntentId || paymentIntent.id });
      onPaid(r.vendor);
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Box sx={{ p: 2, border: '1px solid #d7d2c6', borderRadius: 1.5, mb: 2, bgcolor: '#fff' }}>
        <CardElement options={{ hidePostalCode: false, style: { base: { fontSize: '16px', color: '#222', '::placeholder': { color: '#aab7c4' } } } }} />
      </Box>
      <Button fullWidth size="large" variant="contained" disabled={!stripe || busy} onClick={pay} sx={{ height: 52, bgcolor: '#d40100', '&:hover': { bgcolor: '#a30000' } }}>
        {busy ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : `${label} · ${money(amountCents)}`}
      </Button>
      <Typography variant="caption" color="text.secondary" display="block" mt={1} textAlign="center">Pago seguro con tarjeta procesado por Stripe.</Typography>
    </Box>
  );
}

export default function CsccVendorPayPage() {
  const token = new URLSearchParams(window.location.search).get('t') || '';
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);

  // Formulario local
  const [days, setDays] = useState([]);
  const [fullPackage, setFullPackage] = useState(false);
  const [info, setInfo] = useState({ representative: '', phone: '', products: '', electricity: '', water: '' });
  const [accept, setAccept] = useState(false);
  const [payKind, setPayKind] = useState('deposit');
  const [justPaid, setJustPaid] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [docMsg, setDocMsg] = useState('');

  const uploadDoc = async (docType, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setDocMsg('El archivo pasa de 10 MB.'); return; }
    setUploadingDoc(docType);
    setDocMsg('');
    try {
      const dataBase64 = await fileToBase64(file);
      const { vendor: v } = await callFn(FN_URLS.uploadDoc, { token, docType, fileName: file.name, contentType: file.type, dataBase64 });
      setVendor(v);
      setDocMsg(`${DOC_TYPES[docType]} recibido. ¡Gracias!`);
    } catch (e) {
      setDocMsg(e.message);
    } finally {
      setUploadingDoc(null);
    }
  };

  useEffect(() => {
    if (!token) { setError('Este enlace no es válido.'); setLoading(false); return; }
    callFn(FN_URLS.get, { token })
      .then(({ vendor: v }) => {
        setVendor(v);
        setDays(v.days || []);
        setFullPackage(v.fullPackage);
        setInfo({ representative: v.representative || '', phone: v.phone || '', products: v.products || '', electricity: v.electricity || '', water: v.water || '' });
        setAccept(Boolean(v.acceptedTermsAt));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const totals = useMemo(() => (vendor ? computeTotals(vendor, { days, fullPackage }) : null), [vendor, days, fullPackage]);
  const allDays = (vendor && vendor.pricing && vendor.pricing.days) || [];
  const sumAllDays = allDays.reduce((s, d) => s + Math.round(Number(vendor.pricing.dayPrices[d.key] || 0) * 100), 0);
  const packageCents = vendor ? Math.round(Number(vendor.pricing.packagePrice || 0) * 100) : 0;

  // Si marca los 4 días sueltos y el paquete es más barato, se aplica el paquete.
  useEffect(() => {
    if (!vendor || vendor.daysLocked) return;
    if (!fullPackage && allDays.length > 0 && days.length === allDays.length && packageCents > 0 && packageCents < sumAllDays) setFullPackage(true);
  }, [days, fullPackage, vendor, allDays.length, packageCents, sumAllDays]);

  const saveSelection = async () => {
    setSaving(true);
    setError('');
    try {
      const { vendor: v } = await callFn(FN_URLS.save, { token, days, fullPackage, ...info, acceptTerms: accept });
      setVendor(v);
      setDays(v.days || []);
      setFullPackage(v.fullPackage);
      return v;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Container sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Container>;
  if (!vendor) return <Container maxWidth="sm" sx={{ py: 10 }}><Alert severity="error">{error || 'Este enlace no es válido.'}</Alert></Container>;

  const paid = vendor.paidCents || 0;
  const remaining = vendor.remainingCents || 0;
  const readyToPay = vendor.totalCents > 0 && vendor.acceptedTermsAt && totals.totalCents === vendor.totalCents && totals.fullPackage === vendor.fullPackage && JSON.stringify(totals.days) === JSON.stringify(vendor.days);
  const fullOnly = vendor.depositCents >= vendor.totalCents; // p. ej. artesanos: pago completo
  const effectiveKind = fullOnly ? 'full' : payKind;
  const payAmount = paid === 0 ? (effectiveKind === 'deposit' ? vendor.depositCents : vendor.totalCents) : remaining;
  const isFlat = vendor.flatPrice > 0;
  const pendingDocs = missingDocs(vendor);
  const tax = taxBreakdown(totals.totalCents);

  return (
    <>
      <Helmet><title>{vendor.eventName} · Espacio en The Village</title></Helmet>
      <Box sx={{ minHeight: '100vh', bgcolor: '#F4F6F8', py: { xs: 3, md: 6 } }}>
        <Container maxWidth="sm">
          <Stack alignItems="center" mb={3}><Logo disabledLink /></Stack>
          <Paper elevation={0} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
            <Typography variant="overline" color="text.secondary">{vendor.eventName}</Typography>
            <Typography variant="h4" sx={{ mb: 0.5 }}>{vendor.business}</Typography>
            <Typography variant="body2" color="text.secondary">{vendor.typeLabel} · {vendor.venue}</Typography>
            <Box mt={1.5}>
              <Chip size="small" color={vendor.status === 'paid' ? 'success' : vendor.status === 'deposit_paid' ? 'info' : vendor.status === 'cancelled' ? 'error' : 'warning'}
                label={{ paid: 'Pagado en su totalidad', deposit_paid: 'Depósito pagado · balance pendiente', pending_deposit: 'Depósito pendiente', cancelled: 'Participación cancelada' }[vendor.status] || vendor.status} />
            </Box>

            {vendor.status === 'cancelled' && <Alert severity="error" sx={{ mt: 3 }}>Esta participación fue cancelada. Comunícate con la organización si tienes preguntas.</Alert>}

            {justPaid && (
              <Alert severity="success" sx={{ mt: 3 }}>
                <strong>¡Pago recibido!</strong> {KIND_LABELS[justPaid.kind] || 'Pago'} de {money(justPaid.amountCents)} con {METHOD_LABELS[justPaid.method] || 'tarjeta'}. Te enviamos el recibo a {vendor.email}.
                {remaining > 0 && <> Queda un balance de <strong>{money(remaining)}</strong> con vencimiento el {fmtDateEs(vendor.balanceDueDate)}; puedes pagarlo en este mismo enlace.</>}
              </Alert>
            )}

            {vendor.status !== 'cancelled' && (
              <>
                {/* ---- Días ---- */}
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>1. Días de participación</Typography>
                {vendor.daysLocked ? (
                  <Typography variant="body2" color="text.secondary">{paid > 0 ? 'Los días quedaron fijados con tu pago.' : 'Los días fueron fijados por la organización.'} Para cambios, comunícate con {vendor.contactName || 'la organización'}.</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">{isFlat ? <>Tarifa única de <strong>{money(Math.round(vendor.flatPrice * 100))}</strong> por todo el evento. Marca los días en que participarás para que la organización sepa cuándo contar contigo.</> : <>Marca el día o los días en que participarás. {packageCents > 0 && packageCents < sumAllDays && <>Si vas todos los días, el paquete cuesta {money(packageCents)} en vez de {money(sumAllDays)}.</>}</>}</Typography>
                )}
                <Stack mt={1.5} spacing={0.5}>
                  {allDays.map((d) => (
                    <FormControlLabel key={d.key} disabled={vendor.daysLocked || fullPackage}
                      control={<Checkbox checked={fullPackage || days.includes(d.key)} onChange={(e) => setDays(e.target.checked ? [...days, d.key] : days.filter((k) => k !== d.key))} />}
                      label={<Stack direction="row" justifyContent="space-between" sx={{ width: { xs: 220, sm: 320 } }}><span>{d.label}</span>{!isFlat && <strong>{money(Math.round(Number(vendor.pricing.dayPrices[d.key] || 0) * 100))}</strong>}</Stack>} />
                  ))}
                  {packageCents > 0 && !isFlat && (
                    <FormControlLabel disabled={vendor.daysLocked}
                      control={<Switch checked={fullPackage} onChange={(e) => { setFullPackage(e.target.checked); if (e.target.checked) setDays(allDays.map((d) => d.key)); }} />}
                      label={<Stack direction="row" justifyContent="space-between" sx={{ width: { xs: 220, sm: 320 } }}><span>Paquete todos los días</span><strong>{money(packageCents)}</strong></Stack>} />
                  )}
                </Stack>

                <Box sx={{ bgcolor: '#F4F6F8', borderRadius: 2, p: 2, mt: 2 }}>
                  <Stack direction="row" justifyContent="space-between"><Typography>Costo total del espacio</Typography><Typography fontWeight="bold">{money(totals.totalCents)}</Typography></Stack>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="caption" color="text.secondary">Subtotal {money(tax.subtotalCents)} + IVU 11.5% {money(tax.taxCents)} (el precio ya incluye el IVU)</Typography></Stack>
                  {totals.balanceCents === 0 ? (
                    <Stack direction="row" justifyContent="space-between" mt={0.5}><Typography variant="body2" color="text.secondary">Pago completo · vence el {fmtDateEs(vendor.depositDueDate)}</Typography><Typography variant="body2">{money(totals.totalCents)}</Typography></Stack>
                  ) : (<>
                    <Stack direction="row" justifyContent="space-between" mt={0.5}><Typography variant="body2" color="text.secondary">Depósito ({vendor.depositPercent}%) · vence el {fmtDateEs(vendor.depositDueDate)}</Typography><Typography variant="body2">{money(totals.depositCents)}</Typography></Stack>
                    <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">Balance · vence el {fmtDateEs(vendor.balanceDueDate)}</Typography><Typography variant="body2">{money(totals.balanceCents)}</Typography></Stack>
                  </>)}
                  {paid > 0 && <>
                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Pagado</Typography><Typography variant="body2" color="success.main" fontWeight="bold">{money(paid)}</Typography></Stack>
                    <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Pendiente</Typography><Typography variant="body2" fontWeight="bold">{money(remaining)}</Typography></Stack>
                  </>}
                </Box>

                {/* ---- Datos ---- */}
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>2. Datos del participante</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Representante" value={info.representative} onChange={(e) => setInfo({ ...info, representative: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Teléfono" value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} /></Grid>
                  <Grid item xs={12}><TextField fullWidth size="small" label={['emprendedor', 'artesano'].includes(vendor.type) ? 'Productos que ofrecerá' : 'Tipo de comida / menú'} value={info.products} onChange={(e) => setInfo({ ...info, products: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" select label="Electricidad" value={info.electricity} onChange={(e) => setInfo({ ...info, electricity: e.target.value })}>{ELECTRICITY.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}</TextField></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" select label="Agua" value={info.water} onChange={(e) => setInfo({ ...info, water: e.target.value })}>{WATER.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}</TextField></Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>Email de contacto: {vendor.email}. Cada participante trae su carpa o food truck, mesas, sillas, equipo y reserva de agua.</Typography>

                {/* ---- Condiciones ---- */}
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>3. Condiciones</Typography>
                <Typography variant="body2" color="text.secondary">{vendor.cancelTerms}</Typography>
                {vendor.agreementUrl && <Typography variant="body2" mt={1}><a href={vendor.agreementUrl} target="_blank" rel="noreferrer" style={{ color: '#d40100' }}>Ver el acuerdo completo (PDF)</a></Typography>}
                <FormControlLabel sx={{ mt: 1 }} disabled={Boolean(vendor.acceptedTermsAt)} control={<Checkbox checked={accept} onChange={(e) => setAccept(e.target.checked)} />}
                  label={vendor.acceptedTermsAt ? `Condiciones aceptadas el ${moment(vendor.acceptedTermsAt).format('D/M/YYYY HH:mm')}` : 'He leído y acepto las condiciones de participación y la modalidad seleccionada.'} />

                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>4. Documentos</Typography>
                <Typography variant="body2" color="text.secondary"><strong>Obligatorio antes de pagar.</strong> Sube aquí el acuerdo firmado{vendor.agreementUrl ? <> (<a href={vendor.agreementUrl} target="_blank" rel="noreferrer" style={{ color: '#d40100' }}>descárgalo aquí</a>, fírmalo y súbelo en PDF o foto)</> : ' (PDF o foto)'}, tu registro de comerciante{vendor.type === 'artesano' ? ' y, si lo tienes, tu certificado de artesano (opcional)' : ''}. Máximo 10 MB por archivo.</Typography>
                {docMsg && <Alert severity={/recibido/.test(docMsg) ? 'success' : 'error'} sx={{ mt: 1.5 }} onClose={() => setDocMsg('')}>{docMsg}</Alert>}
                <Stack spacing={1.5} mt={2}>
                  {DOC_KEYS.map((k) => {
                    const d = (vendor.documents || {})[k];
                    const optional = k === 'artisanCertificate';
                    if (optional && !d && vendor.type !== 'artesano') return null;
                    return (
                      <Stack key={k} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ p: 1.5, border: '1px solid #e5e1d8', borderRadius: 1.5 }}>
                        <Box>
                          <Typography variant="subtitle2">{DOC_TYPES[k]}{optional ? ' (opcional)' : ' *'}</Typography>
                          {d ? <Typography variant="caption" color="success.main">Recibido · <a href={d.url} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{d.name}</a></Typography> : <Typography variant="caption" color="text.secondary">Pendiente</Typography>}
                        </Box>
                        <Button variant={d ? 'text' : 'outlined'} size="small" component="label" disabled={uploadingDoc === k} sx={{ color: '#d40100', borderColor: '#d40100' }}>
                          {uploadingDoc === k ? <CircularProgress size={16} /> : (d ? 'Reemplazar' : 'Subir archivo')}
                          <input hidden type="file" accept="application/pdf,image/*" onChange={(e) => { uploadDoc(k, e.target.files && e.target.files[0]); e.target.value = ''; }} />
                        </Button>
                      </Stack>
                    );
                  })}
                </Stack>

                {/* ---- Pago ---- */}
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>5. Pago</Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

                {remaining === 0 && vendor.totalCents > 0 ? (
                  <Alert severity="success">Tu espacio está pagado en su totalidad. ¡Nos vemos en The Village!</Alert>
                ) : pendingDocs.length > 0 ? (
                  <Alert severity="warning">Para poder pagar primero sube: <strong>{pendingDocs.map((k) => DOC_TYPES[k]).join(', ')}</strong> (sección 4).</Alert>
                ) : !readyToPay ? (
                  <Stack spacing={1.5}>
                    <Typography variant="body2" color="text.secondary">Confirma tu selección para continuar al pago.</Typography>
                    <Button variant="contained" size="large" disabled={saving || totals.totalCents <= 0 || !accept} onClick={async () => { const v = await saveSelection(); if (v) setNotice('Selección guardada. Ya puedes pagar.'); }} sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#a30000' } }}>
                      {saving ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Confirmar selección'}
                    </Button>
                    {totals.totalCents <= 0 && <Typography variant="caption" color="error">Selecciona al menos un día.</Typography>}
                    {totals.totalCents > 0 && !accept && <Typography variant="caption" color="error">Debes aceptar las condiciones.</Typography>}
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    {paid === 0 && fullOnly && <Typography variant="body2">Pago completo: <strong>{money(vendor.totalCents)}</strong> · vence el {fmtDateEs(vendor.depositDueDate)}.</Typography>}
                    {paid === 0 && !fullOnly && (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button fullWidth variant={payKind === 'deposit' ? 'contained' : 'outlined'} onClick={() => setPayKind('deposit')} sx={payKind === 'deposit' ? { bgcolor: '#d40100' } : { color: '#d40100', borderColor: '#d40100' }}>Depósito {vendor.depositPercent}% · {money(vendor.depositCents)}</Button>
                        <Button fullWidth variant={payKind === 'full' ? 'contained' : 'outlined'} onClick={() => setPayKind('full')} sx={payKind === 'full' ? { bgcolor: '#d40100' } : { color: '#d40100', borderColor: '#d40100' }}>Pagar todo · {money(vendor.totalCents)}</Button>
                      </Stack>
                    )}
                    {paid === 0 && !fullOnly && payKind === 'deposit' && <Typography variant="caption" color="text.secondary">El balance de {money(vendor.balanceCents)} se paga en este mismo enlace en o antes del {fmtDateEs(vendor.balanceDueDate)}.</Typography>}
                    {paid > 0 && <Typography variant="body2">Balance pendiente: <strong>{money(remaining)}</strong> · vence el {fmtDateEs(vendor.balanceDueDate)}.</Typography>}
                    <Elements stripe={stripePromise}>
                      <CardForm token={token} kind={paid === 0 ? effectiveKind : 'balance'} amountCents={payAmount}
                        label={paid === 0 ? (effectiveKind === 'deposit' ? 'Pagar depósito' : 'Pagar total') : 'Pagar balance'}
                        onPaid={(v) => { setVendor(v); const last = (v.payments || [])[v.payments.length - 1]; setJustPaid(last); setError(''); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        onError={(m) => setError(m)} />
                    </Elements>
                    {!vendor.daysLocked && <Typography variant="caption" color="text.secondary">¿Cambiaste de idea con los días? Ajusta la selección arriba y vuelve a confirmar antes de pagar.</Typography>}
                  </Stack>
                )}

                {vendor.payments && vendor.payments.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle2" gutterBottom>Pagos registrados</Typography>
                    {vendor.payments.map((p, i) => (
                      <Typography key={i} variant="body2" color="text.secondary">{p.paidAt ? moment(p.paidAt).format('D/M/YYYY') : ''} · {KIND_LABELS[p.kind] || p.kind} · {METHOD_LABELS[p.method] || p.method} · {money(p.amountCents)}</Typography>
                    ))}
                  </>
                )}
              </>
            )}

            <Divider sx={{ my: 3 }} />
            <Typography variant="body2" color="text.secondary" textAlign="center">Dudas: {vendor.contactName || 'B&B Target Center'}{vendor.contactPhone ? ` · ${vendor.contactPhone}` : ''}{vendor.contactEmail ? ` · ${vendor.contactEmail}` : ''}</Typography>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
